import Api, {
  bumpApiSessionGen,
  clearApiAuthToken,
  peekApiSessionGen,
  tenantHashDaSessao,
  setApiAuthToken,
} from "@/services/Api";
import {
  definirEmpresaHashAtual,
  gravarUserDataFetchedAt,
} from "@/services/docAssinadoLog";
import { FaceBiometria } from "@/services/FaceBiometria";
import { FaceRecognition } from "@/services/FaceRecognition";
import {
  deleteJSONFile,
  readJSONFile,
  writeJSONFile,
} from "@/services/FileStorage";
import { NotificationService } from "@/services/NotificationService";
import {
  apagarListasCache,
  gravarListaCache,
  lerListaCache,
} from "@/services/CacheListas";
import { garantirCatalogoCidNoAparelho } from "@/services/CidCatalogo";
import { SyncService } from "@/services/Sync";
import AsyncStorage from "@/platform/async-storage";
import * as Application from "@/platform/application";
import { router } from "@/navigation";
import Constants from "@/platform/constants";
import * as Location from "@/platform/location";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MaterialIcons } from "@/ui/icons";
import { Alert } from "@/platform/alert";
import { AppState } from "@/platform/app-state";
import { Platform } from "@/platform/platform";
import { esquecerSaidaParaLogin } from "@/platform/saida-do-app";
import { Modal } from "@/ui/Modal";
import s from "./AuthContext.module.css";
import { T, TJ } from "@/utils/i18n";

const AUTH_TOKEN_STORAGE_KEY = "@tirvumais:token";
const AUTH_ID_STORAGE_KEY = "@tirvumais:id";
const AUTH_USER_DATA_KEY = "@tirvumais:userData";
// Postos, veículos, pessoas, checklists e bancos NÃO moram mais no AsyncStorage: ver
// services/CacheListas.ts (no Android, valor > ~2 MB não volta na leitura e derrubava o boot).
const AUTH_BIOMETRICS_SETUP_KEY = "@tirvumais:biometrics_setup_done";
const AUTH_FCM_TOKEN_KEY = "@tirvumais:fcm_token";

// --- t-credentials: throttle de 24h por domínio (ronda/livro/os) e por colaborador ---
const TC_TTL_MS = 24 * 60 * 60 * 1000;
const TC_LASTFETCH_PREFIX = "@tirvumais:tc_lastfetch:";
// Chave POR DOMÍNIO E POR COLABORADOR — evita vazar a janela de 24h entre empresas/colaboradores.
const tcKey = (
  dominio: string,
  idColaborador: string | number | null | undefined,
) => `${TC_LASTFETCH_PREFIX}${dominio}:${idColaborador ?? ""}`;

/**
 * Carga do perfil (/iniciar) quando NÃO há userData nenhum para mostrar — 1º login ou
 * cache apagado. Com internet fraca o login passa (resposta pequena) mas o /iniciar não:
 * as telas ficam no skeleton e, depois de uma tentativa que falhou, mostram o aviso com
 * "Tentar novamente" (components/AvisoCargaPerfil). Com userData na tela, não é usado.
 *
 * `motivo`: "conexao" = a requisição não completou (sem rede, tempo esgotado, rede que
 * devolve página); "servidor" = a nossa API ou o proxy dela responderam indisponível
 * (JSON: 503, 404 do proxy); "recusa" = a API respondeu um erro de negócio (`mensagem`
 * traz o texto dela).
 */
export type CargaPerfil = {
  tentando: boolean;
  falhou: boolean;
  motivo: "conexao" | "servidor" | "recusa" | null;
  mensagem: string | null;
};

const CARGA_PERFIL_INICIAL: CargaPerfil = {
  tentando: false,
  falhou: false,
  motivo: null,
  mensagem: null,
};

type AuthContextValue = {
  token: string | null;
  idColaborador: string | null;
  userData: any | null;
  cargaPerfil: CargaPerfil;
  postos: any[] | null;
  veiculos: any[] | null;
  pessoas: any[] | null;
  checklists: any[] | null;
  bancos: { id: any; nome: string; codigo?: string }[] | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  isBiometricsSetupDone: boolean;
  location: { latitude: number; longitude: number } | null;
  fcmToken: string | null;
  signIn: (token: string, idColaborador?: string) => Promise<void>;
  signOut: () => Promise<void>;
  setUserData: (data: any) => Promise<void>;
  refreshUserData: () => Promise<any | null>;
  refreshStaticData: () => Promise<void>;
  carregarDominioTcred: (
    dominio: "ronda" | "livro" | "os",
    opts?: { force?: boolean },
  ) => Promise<void>;
  ensureBancosLoaded: () => Promise<void>;
  ensureChecklistsLoaded: () => Promise<void>;
  refreshLocation: () => Promise<{
    latitude: number;
    longitude: number;
  } | null>;
  markBiometricsSetupDone: () => Promise<void>;
  clearAuth: () => Promise<void>;
  setFcmToken: (token: string | null) => Promise<void>;
  syncFirebaseToken: (idColaborador?: number | string | null) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [idColaborador, setIdColaborador] = useState<string | null>(null);
  const [userData, setUserDataState] = useState<any | null>(null);
  const [postos, setPostos] = useState<any[] | null>(null);
  const [veiculos, setVeiculos] = useState<any[] | null>(null);
  const [pessoas, setPessoas] = useState<any[] | null>(null);
  const [checklists, setChecklists] = useState<any[] | null>(null);
  const [bancos, setBancos] = useState<
    { id: any; nome: string; codigo?: string }[] | null
  >(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isBiometricsSetupDone, setIsBiometricsSetupDone] = useState(false);
  const [location, setLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [fcmToken, setFcmTokenState] = useState<string | null>(null);
  const [cargaPerfil, setCargaPerfil] =
    useState<CargaPerfil>(CARGA_PERFIL_INICIAL);
  // Espelha o userData mais recente para merges fora do ciclo de render (ex.: o merge
  // por-domínio do t-credentials), evitando spread de um estado obsoleto preso em closure.
  const userDataRef = useRef<any | null>(null);
  // Token da sessão para o refreshUserData/refreshStaticData, gravado JUNTO com cada
  // setToken (bootstrap, signIn, signOut, clearAuth) — nunca espelhado no render. O login
  // (Onboarding.navegarAposLogin) chama o refreshUserData logo após o `await signIn`, com a
  // closure do render ANTERIOR (token nulo): lendo o estado, ele saía sem buscar nada.
  const tokenRef = useRef<string | null>(null);
  // GERAÇÃO DE SESSÃO (contador em services/Api.tsx — peek/bumpApiSessionGen):
  // incrementada SINCRONAMENTE na entrada de signIn/signOut/clearAuth. Todo fluxo
  // assíncrono que persiste userData (refreshUserData, carregarDominioTcred,
  // refreshStaticData, setUserData — e o syncAll, no Sync.tsx) captura o valor no início
  // e ABORTA a gravação se ele mudou no meio: um refresh em voo que termina DEPOIS de um
  // signOut "ressuscitaria" o userData da sessão morta (ex.: forcar_logoff='S' capturado
  // antes do rearme), e um em voo através de um trocarEmpresa (signIn SEM signOut)
  // vazaria dados da empresa anterior para a sessão nova.
  const refreshUserDataInFlightRef = useRef(false);
  // Promise da chamada em andamento: chamadas concorrentes aguardam o MESMO fetch
  // (em vez de receberem o userData atual/obsoleto na hora) — evita flash de tela vazia.
  const refreshUserDataPromiseRef = useRef<Promise<any> | null>(null);
  // Geração de sessão da chamada em voo: o dedup acima só vale DENTRO da mesma sessão.
  // Pós signIn/signOut, a promise velha (que vai abortar sem gravar) não pode ser
  // entregue à sessão nova — senão a Home ficaria sem o primeiro fetch pós-relogin.
  const refreshUserDataSessionGenRef = useRef(0);
  const refreshStaticDataInFlightRef = useRef(false);
  const refreshBancosInFlightRef = useRef(false);
  const refreshChecklistsInFlightRef = useRef(false);

  const refreshLocation = useCallback(async () => {
    try {
      const permission = await Location.getForegroundPermissionsAsync();
      if (permission.status !== "granted") {
        setLocation(null);
        return null;
      }

      const lastKnownLocation = await Location.getLastKnownPositionAsync({
        maxAge: 15 * 60 * 1000,
        requiredAccuracy: 3000,
      });

      if (lastKnownLocation) {
        const cachedLocation = {
          latitude: lastKnownLocation.coords.latitude,
          longitude: lastKnownLocation.coords.longitude,
        };
        setLocation(cachedLocation);
      }

      try {
        const currentLocation = await Location.getCurrentPositionAsync({
          accuracy: Location.Accuracy.High,
        });

        const nextLocation = {
          latitude: currentLocation.coords.latitude,
          longitude: currentLocation.coords.longitude,
        };

        setLocation(nextLocation);
        return nextLocation;
      } catch {
        if (lastKnownLocation) {
          return {
            latitude: lastKnownLocation.coords.latitude,
            longitude: lastKnownLocation.coords.longitude,
          };
        }
      }

      return null;
    } catch (error) {
      console.error("Erro ao atualizar localização:", error);
      return null;
    }
  }, []);

  const parseStoredUserData = useCallback((storedUserData: string | null) => {
    if (!storedUserData) {
      return null;
    }

    try {
      return JSON.parse(storedUserData);
    } catch (error) {
      console.error("Erro ao ler userData salvo:", error);
      return null;
    }
  }, []);

  useEffect(() => {
    const bootstrapAuth = async () => {
      try {
        // 1) SESSÃO, sozinha. Só as chaves PEQUENAS, cada leitura isolada. Até 05/10/2026 o token
        //    era lido no MESMO Promise.all das listas (postos/veículos/pessoas/checklists/bancos):
        //    no Android, uma lista acima de ~2 MB não volta da leitura ("Row too big to fit into
        //    CursorWindow"), o Promise.all inteiro falhava e o token — que continuava gravado —
        //    não era restaurado. O app reabria deslogado e pedia o CPF a cada abertura.
        //    Erro de leitura do token tenta de novo (nulo é "sem sessão", não erro): uma falha
        //    passageira de I/O nunca pode virar logout.
        const lerChavePequena = async (
          chave: string,
          tentativas = 1,
        ): Promise<string | null> => {
          for (let i = 1; ; i++) {
            try {
              return await AsyncStorage.getItem(chave);
            } catch (e) {
              if (i >= tentativas) {
                console.warn(`[Auth] Boot: leitura de ${chave} falhou:`, e);
                return null;
              }
              await new Promise((r) => setTimeout(r, 150));
            }
          }
        };
        const [storedToken, storedId, storedBiometricsSetup, storedFcmToken] =
          await Promise.all([
            lerChavePequena(AUTH_TOKEN_STORAGE_KEY, 3),
            lerChavePequena(AUTH_ID_STORAGE_KEY, 3),
            lerChavePequena(AUTH_BIOMETRICS_SETUP_KEY),
            lerChavePequena(AUTH_FCM_TOKEN_KEY),
          ]);

        // RESTAURA A SESSÃO PRIMEIRO (token/id), ANTES de qualquer leitura de arquivo, Sync ou
        // JSON.parse que possa LANÇAR. CRÍTICO: isAuthenticated = !!token, então a sessão NUNCA
        // pode se perder por um erro ao hidratar OUTROS dados. Antes o setToken vinha DEPOIS de
        // readJSONFile/applyLocalDocStatus/JSON.parse — se o código NOVO de uma atualização
        // topasse num cache ANTIGO e lançasse, o token jamais era restaurado e o usuário caía no
        // login a CADA update. Agora a sessão é restaurada primeiro e o resto é best-effort.
        const normalizedToken = storedToken?.trim() ? storedToken : null;
        const normalizedId = storedId?.trim() ? storedId : null;
        setApiAuthToken(normalizedToken);
        tokenRef.current = normalizedToken;
        setToken(normalizedToken);
        setIdColaborador(normalizedId);
        setIsBiometricsSetupDone(storedBiometricsSetup === "true");
        setFcmTokenState(storedFcmToken?.trim() ? storedFcmToken : null);

        // userData (best-effort): qualquer falha aqui NÃO derruba a sessão.
        try {
          let normalizedUserData = await readJSONFile("userData.json");

          if (!normalizedUserData) {
            const storedUserDataFromAsync =
              await AsyncStorage.getItem(AUTH_USER_DATA_KEY);
            if (storedUserDataFromAsync) {
              normalizedUserData = parseStoredUserData(storedUserDataFromAsync);
              if (normalizedUserData) {
                await writeJSONFile("userData.json", normalizedUserData);
                await AsyncStorage.removeItem(AUTH_USER_DATA_KEY);
              }
            }
          }

          // 🔒 CROSS-TENANT no BOOT (30/07): restart do app depois de uma troca de
          // empresa hidratava o arquivo da empresa ANTERIOR sob o token da atual
          // (e os applyLocal*, escopados no tenant certo, mesclavam por cima —
          // status de assinatura de B pintando doc de A, ids colidem). Descarta.
          if (normalizedUserData) {
            const hArq = String(
              (normalizedUserData as any)?.hash ??
                (normalizedUserData as any)?.done?.hash ??
                "",
            )
              .trim()
              .toUpperCase();
            const hSessao = await tenantHashDaSessao().catch(() => "");
            if (hArq && hSessao && hArq !== hSessao) {
              console.warn(
                "[Auth] 🔒 Boot: userData.json é de OUTRO tenant — descartado (o /iniciar da empresa atual repovoa).",
              );
              normalizedUserData = null;
            }
          }

          // Aplica status local de documentos/notificações — cada um isolado: se o cache
          // antigo (pós-update) fizer um deles lançar, o outro e a sessão seguem normais.
          if (normalizedUserData) {
            try {
              normalizedUserData =
                await SyncService.applyLocalDocStatus(normalizedUserData);
            } catch (e) {
              console.warn("applyLocalDocStatus (hidratação):", e);
            }
            try {
              normalizedUserData =
                await SyncService.applyLocalNotificacoesLidas(
                  normalizedUserData,
                );
            } catch (e) {
              console.warn("applyLocalNotificacoesLidas (hidratação):", e);
            }
          }
          setUserDataState(normalizedUserData);
        } catch (e) {
          console.warn("Falha ao hidratar userData (sessão preservada):", e);
        }

        // 2) Listas em cache (best-effort): do ARQUIVO (services/CacheListas.ts), cada uma
        //    isolada — nenhuma derruba a sessão nem trava o boot. Na 1ª abertura depois da
        //    atualização, a chave antiga do AsyncStorage migra para o arquivo ou, se for a
        //    grande demais para ler, é apagada (o /iniciar repõe a lista).
        const [cPostos, cVeiculos, cPessoas, cChecklists, cBancos] =
          await Promise.all([
            lerListaCache("postos"),
            lerListaCache("veiculos"),
            lerListaCache("pessoas"),
            lerListaCache("checklists"),
            lerListaCache("bancos"),
          ]);
        setPostos(cPostos ?? []);
        setVeiculos(cVeiculos ?? []);
        setPessoas(cPessoas ?? []);
        setChecklists(cChecklists ?? []);
        setBancos(cBancos);
      } catch (error) {
        console.error(
          "Erro no bootstrap de auth (sessão pode ter sido preservada):",
          error,
        );
      } finally {
        setIsHydrated(true);
      }
    };

    bootstrapAuth();
  }, [parseStoredUserData]);

  const setFcmToken = useCallback(async (token: string | null) => {
    try {
      const normalizedToken = token?.trim() ? token : null;
      if (normalizedToken) {
        await AsyncStorage.setItem(AUTH_FCM_TOKEN_KEY, normalizedToken);
      } else {
        await AsyncStorage.removeItem(AUTH_FCM_TOKEN_KEY);
      }
      setFcmTokenState(normalizedToken);
    } catch (error) {
      console.error("Erro ao persistir FCM token:", error);
    }
  }, []);

  /**
   * Atualiza firebase_token + versão instalada do app do colaborador NO SERVIDOR
   * (tenant atual, via t-credentials). Use após signIn/troca de empresa: como o signIn
   * já trocou o token de API (setApiAuthToken), a gravação cai no banco da NOVA empresa.
   * Best-effort (não bloqueia o fluxo). Se ainda não houver fcmToken, tenta obtê-lo.
   */
  const syncFirebaseToken = useCallback(
    async (idParam?: number | string | null) => {
      try {
        let tk = fcmToken;
        if (!tk) {
          tk = await NotificationService.registerForPushNotificationsAsync();
          if (tk) await setFcmToken(tk);
        }
        const id = Number(idParam ?? idColaborador ?? 0) || 0;
        // A ATUALIZAÇÃO DE VERSÃO NÃO depende do firebase token: exige só o id. Antes o
        // guard `!tk` fazia a função sair cedo quando o aparelho não tinha push token
        // (SERVICE_NOT_AVAILABLE) → a versão do app nunca era gravada nesses usuários.
        // O token vai vazio quando não houver; o backend preserva o existente.
        if (id <= 0) return;

        // Versão INSTALADA do app (não a do SO). expo-application lê a versão nativa
        // (CFBundleShortVersionString / versionName), confiável em build standalone;
        // cai para expoConfig.version se indisponível. Usado para saber quem está
        // desatualizado, comparando com VERSAO_APP_ANDROID/VERSAO_APP_IOS (conforme a
        // plataforma) que o /iniciar devolve em userData.versaoAppAndroid/versaoAppIos.
        const appVersion = String(
          Application.nativeApplicationVersion ??
            Constants.expoConfig?.version ??
            "",
        );

        // t-credentials grava direto na tab_adm_colaboradores; o id vem do TOKEN.
        // WEB: Platform.OS === "web" → androidVersion/iosVersion vão vazios (o backend
        // preserva os existentes); só o firebaseToken é atualizado a partir do browser.
        await Api.geral.atualizarVersaoColaborador({
          firebaseToken: tk ?? "",
          androidVersion: Platform.OS === "android" ? appVersion : "",
          iosVersion: Platform.OS === "ios" ? appVersion : "",
        });
      } catch (error) {
        console.warn("[Auth] Falha ao atualizar firebase token:", error);
      }
    },
    [fcmToken, idColaborador, setFcmToken],
  );

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    void refreshLocation();
  }, [isHydrated, refreshLocation]);

  // Watcher contínuo: mantém `location` sempre fresca conforme o usuário se move,
  // para que telas (ex.: Câmera/Ponto) leiam a posição instantaneamente, sem esperar
  // um novo fix de GPS. À prova de erro:
  //   - dedup de jitter (<~5m) para não re-renderizar os consumidores do contexto à toa;
  //   - errorHandler que recupera o watcher se ele morrer (GPS off, permissão revogada);
  //   - re-arma ao voltar ao foreground (cobre permissão concedida DEPOIS do boot).
  useEffect(() => {
    if (!isHydrated) return;

    let active = true;
    let starting = false;
    let subscription: Location.LocationSubscription | null = null;

    const stop = () => {
      subscription?.remove();
      subscription = null;
    };

    const start = async () => {
      if (!active || subscription || starting) return;
      starting = true;
      try {
        const permission = await Location.getForegroundPermissionsAsync();
        if (!active || permission.status !== "granted") return;

        const sub = await Location.watchPositionAsync(
          {
            accuracy: Location.Accuracy.Balanced,
            timeInterval: 10000,
            distanceInterval: 15,
          },
          (pos) => {
            if (!active) return;
            const next = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
            };
            // Ignora variações desprezíveis (~5m) — evita re-render de todos os
            // consumidores de useAuth() quando a posição praticamente não mudou.
            setLocation((prev) =>
              prev &&
              Math.abs(prev.latitude - next.latitude) < 5e-5 &&
              Math.abs(prev.longitude - next.longitude) < 5e-5
                ? prev
                : next,
            );
          },
          (error) => {
            // O watcher morreu (GPS desligado, permissão revogada, falha do provider).
            // Limpa e recupera com um one-shot; será re-armado no próximo foreground.
            console.warn(
              "[Location] watcher encerrado:",
              (error as any)?.message ?? error,
            );
            stop();
            void refreshLocation();
          },
        );

        if (!active) {
          sub.remove();
          return;
        }
        subscription = sub;
      } catch {
        // Sem watcher agora — segue com refreshLocation (one-shot) e tenta de novo no foreground.
      } finally {
        starting = false;
      }
    };

    void start();

    // Ao voltar ao foreground: atualiza a posição na hora e (re)inicia o watcher caso
    // ele não esteja ativo (permissão concedida depois, GPS religado, watcher morto).
    const appStateSub = AppState.addEventListener("change", (state: string) => {
      if (state === "active" && active) {
        void refreshLocation();
        void start();
      }
    });

    return () => {
      active = false;
      appStateSub.remove();
      stop();
    };
  }, [isHydrated, refreshLocation]);

  const ensureBancosLoaded = useCallback(async () => {
    if (refreshBancosInFlightRef.current) return;
    if (bancos != null) return;

    refreshBancosInFlightRef.current = true;
    // GERAÇÃO DE SESSÃO: uma troca de empresa no meio da busca não pode pôr no estado (nem no
    // cache) a lista da sessão anterior — e a sessão nova, vendo `bancos != null`, não buscaria.
    const sessionGen = peekApiSessionGen();
    const sessaoMudou = () => peekApiSessionGen() !== sessionGen;
    try {
      // 1) Cache local
      const cached = await lerListaCache("bancos");
      if (sessaoMudou()) return;
      if (cached && cached.length > 0) {
        setBancos(cached);
        return;
      }

      // 2) API — sem token não há como buscar; resolve com lista vazia p/ não travar o loader
      if (!token?.trim()) {
        setBancos([]);
        return;
      }

      const r = await Api.colaboradores.listaBancos();
      if (sessaoMudou()) return;
      if (r.success && Array.isArray(r.done)) {
        const lista = (r.done as any[]).map((b, i) => ({
          // id interno (usado para salvar/casar com o banco do colaborador)
          id: b.id_adm_cadastro_banco ?? b.id ?? i,
          nome:
            b.nome ||
            b.descricao ||
            b.nm_banco ||
            b.razao_social ||
            `Banco ${i + 1}`,
          // código do banco (ex.: 001) — para exibição
          codigo: String(
            b.numero_formatado ?? b.numero ?? b.codigo ?? b.cd_banco ?? "",
          ).trim(),
        }));
        setBancos(lista);
        // Vazia não vira cache: senão nunca mais buscaria (o boot devolve [] e não null).
        if (lista.length > 0) await gravarListaCache("bancos", lista, sessionGen);
      } else {
        // Falha da API: resolve com lista vazia para encerrar o loader (evita carregar infinito)
        setBancos([]);
      }
    } catch {
      if (!sessaoMudou()) setBancos([]);
    } finally {
      refreshBancosInFlightRef.current = false;
    }
  }, [bancos, token]);

  // Garante os templates de checklist (usados pela Ronda virtual). Diferente de bancos,
  // a hidratação pode deixar `checklists` como [] (vazio) — por isso tratamos lista VAZIA
  // como "não carregado" e buscamos na API. Sem isso, a Ronda virtual com checklist
  // vinculado não abre o checklist (checklists vazio → checklistSchema null).
  const ensureChecklistsLoaded = useCallback(async () => {
    if (refreshChecklistsInFlightRef.current) return;
    if (checklists != null && checklists.length > 0) return;

    refreshChecklistsInFlightRef.current = true;
    // GERAÇÃO DE SESSÃO: mesma regra do ensureBancosLoaded (troca de empresa no meio da busca).
    const sessionGen = peekApiSessionGen();
    try {
      // 1) Cache local
      const cached = await lerListaCache("checklists");
      if (peekApiSessionGen() !== sessionGen) return;
      if (cached && cached.length > 0) {
        setChecklists(cached);
        return;
      }

      if (!token?.trim()) return;

      const r = await Api.geral.obterChecklists();
      if (peekApiSessionGen() !== sessionGen) return;
      if (r.success && Array.isArray(r.done) && r.done.length > 0) {
        setChecklists(r.done);
        await gravarListaCache("checklists", r.done, sessionGen);
      }
    } catch {
    } finally {
      refreshChecklistsInFlightRef.current = false;
    }
  }, [checklists, token]);

  // Mantém userDataRef sempre apontando para o estado atual (qualquer origem:
  // bootstrap, refreshUserData, setUserData, merge de domínio).
  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  const setUserData = useCallback(async (data: any) => {
    const sessionGen = peekApiSessionGen();
    try {
      if (data) {
        await writeJSONFile("userData.json", data);
      } else {
        await deleteJSONFile("userData.json");
      }
    } catch (error) {
      // Gravação falhou (armazenamento cheio, I/O): o dado continua válido e vai para a
      // tela mesmo assim — só não sobrevive a reabrir o app. Antes ele era descartado, e
      // quem estava sem perfil ficava no skeleton com o /iniciar já baixado na mão.
      console.error("Erro ao persistir userData:", error);
    }
    // Sessão acabou/trocou DURANTE a gravação (signOut/signIn no meio do await):
    // não regrava ref/estado — o signOut já os limpou, e setar aqui ressuscitaria
    // um userData de sessão morta com token nulo. O ARQUIVO não fica sujo: o
    // deleteJSONFile do signOut entrou na fila de I/O depois desta escrita.
    if (peekApiSessionGen() !== sessionGen) return;
    userDataRef.current = data;
    setUserDataState(data);
    // Registra o hash da empresa ATUAL (o setter existia mas estava sem
    // chamador — o cache de tenant NUNCA atualizava no trocarEmpresa, um dos
    // elos do vazamento cross-tenant de 21-23/07). Best-effort, pós-guard.
    const hashEmpresa = String(
      (data as any)?.hash ?? (data as any)?.done?.hash ?? "",
    ).trim();
    if (hashEmpresa) definirEmpresaHashAtual(hashEmpresa).catch(() => {});
  }, []);

  // Resolve o id do colaborador atual a partir do estado/ref de auth ou do userData.
  const resolveIdColaborador = useCallback((): string | number | null => {
    if (idColaborador) return idColaborador;
    const ud = userDataRef.current;
    const candidate =
      ud?.idColaborador ??
      ud?.id_adm_colaborador ??
      ud?.done?.idColaborador ??
      ud?.done?.id_adm_colaborador ??
      ud?.colaborador?.idColaborador ??
      ud?.colaborador?.id_adm_colaborador ??
      null;
    return candidate != null ? candidate : null;
  }, [idColaborador]);

  // --- throttle helpers (best-effort: em dúvida/erro → permite buscar) ---
  const tcShouldFetch = useCallback(async (key: string): Promise<boolean> => {
    try {
      const raw = await AsyncStorage.getItem(key);
      if (!raw) return true;
      const last = Number(raw);
      if (!Number.isFinite(last)) return true;
      return Date.now() - last >= TC_TTL_MS;
    } catch {
      return true;
    }
  }, []);

  const tcMarkFetched = useCallback(async (key: string): Promise<void> => {
    try {
      await AsyncStorage.setItem(key, String(Date.now()));
    } catch {
      // best-effort
    }
  }, []);

  // Remove TODAS as chaves de throttle do t-credentials (são por-id, então varremos
  // por prefixo). Best-effort: usado no signOut/clearAuth.
  const tcClearAllThrottle = useCallback(async (): Promise<void> => {
    try {
      const keys = await AsyncStorage.getAllKeys();
      const toRemove = keys.filter((k: string) => k.startsWith(TC_LASTFETCH_PREFIX));
      if (toRemove.length > 0) {
        await AsyncStorage.multiRemove(toRemove);
      }
    } catch {
      // best-effort
    }
  }, []);

  /**
   * Carrega UM domínio (ronda/livro/os) do t-credentials e faz merge SOMENTE dos campos
   * daquele domínio no userData atual, preservando o resto. Não lança (best-effort).
   * - Sem `force`, respeita o throttle de 24h por domínio+colaborador.
   * - O merge usa o setter funcional (prev) + persiste igual ao setUserData (writeJSONFile),
   *   nunca faz spread de um userData velho preso em closure → sem corrida com o /iniciar.
   */
  const carregarDominioTcred = useCallback(
    async (dominio: "ronda" | "livro" | "os", opts?: { force?: boolean }) => {
      // Captura a geração de sessão NA ENTRADA: o fetch abaixo é longo e roda em
      // background — se a sessão acabar/trocar no meio, NADA pode ser persistido
      // (ver GERAÇÃO DE SESSÃO no topo do provider / peekApiSessionGen).
      const sessionGen = peekApiSessionGen();
      console.log(
        "[BIO-TC] ENTER",
        dominio,
        "force=",
        opts?.force,
        "udNull=",
        !userDataRef.current,
      );
      try {
        const id = resolveIdColaborador();
        const key = tcKey(dominio, id);

        // Gate por acesso: só faz a request se o colaborador tiver acesso à seção
        // (tem_acesso_ronda / tem_acesso_livro / tem_acesso_os). Vale para os DOIS
        // caminhos (disparo automático pós-iniciar e o force das telas).
        const flagField =
          dominio === "ronda"
            ? "tem_acesso_ronda"
            : dominio === "livro"
              ? "tem_acesso_livro"
              : "tem_acesso_os";
        const ud: any = userDataRef.current;
        const rawAcesso = ud?.[flagField] ?? ud?.done?.[flagField];
        const temAcesso =
          rawAcesso === true ||
          rawAcesso === 1 ||
          String(rawAcesso).trim().toUpperCase() === "S" ||
          String(rawAcesso).trim().toLowerCase() === "true";
        console.log(
          "[BIO-TC]",
          dominio,
          "rawAcesso=",
          rawAcesso,
          "temAcesso=",
          temAcesso,
        );
        if (!temAcesso) return;

        // Permissão da EMPRESA por domínio (junto do acesso):
        //  • Ronda: NÃO bloqueia mais por permite_ronda. Esse flag vem CACHEADO (15s) do
        //    /iniciar e depende do ÚLTIMO PONTO, então logo após bater ainda está 'N' e
        //    travava o carregamento da config de ronda — dependência circular: sem
        //    userData.ronda populado, o menu nunca destravava na hora. O rondaCarregar já
        //    filtra pelo cliente do último ponto (volta vazio se não há ronda), e o gate do
        //    menu decide pela config local + flag. tem_acesso_ronda (acima) é o gate real.
        //  • Livro/OS: NÃO requisita quando permite_* === 'N' (bloqueio só no 'N' EXPLÍCITO —
        //    campo ausente/vazio não bloqueia, não regride enquanto o t-credentials não for
        //    redeployado). Vale para os DOIS caminhos (disparo pós-iniciar e o force das telas).
        if (dominio === "livro" || dominio === "os") {
          const permiteField =
            dominio === "livro" ? "permite_livro_ocorrencia" : "permite_os";
          const rawPermite = ud?.[permiteField] ?? ud?.done?.[permiteField];
          const bloqueado =
            String(rawPermite ?? "")
              .trim()
              .toUpperCase() === "N" ||
            rawPermite === false ||
            rawPermite === 0;
          if (bloqueado) return;
        }

        if (!opts?.force) {
          const should = await tcShouldFetch(key);
          if (!should) return;
        }

        let resp: any;
        if (dominio === "ronda") {
          resp = await Api.tcredentials.rondaCarregar();
        } else if (dominio === "livro") {
          resp = await Api.tcredentials.livroCarregar();
        } else {
          resp = await Api.tcredentials.ordemServicoCarregar();
        }

        // DIAGNÓSTICO: mostra exatamente o que o endpoint devolveu por domínio. Para OS,
        // imprime as contagens de ordemServicos/clientes/equipes — assim dá pra distinguir
        // "backend não deployado / SQL vazio" (contagens 0/ausentes) de "wipe no app".
        if (dominio === "os") {
          console.log(
            "[BIO-TC][os] resp.success=",
            resp?.success,
            "offline=",
            resp?.offline,
            "erros=",
            resp?.erros,
            "| done.ordemServicos=",
            Array.isArray(resp?.done?.ordemServicos)
              ? resp.done.ordemServicos.length
              : resp?.done?.ordemServicos,
            "done.clientes=",
            Array.isArray(resp?.done?.clientes)
              ? resp.done.clientes.length
              : resp?.done?.clientes,
            "done.equipes=",
            Array.isArray(resp?.done?.equipes)
              ? resp.done.equipes.length
              : resp?.done?.equipes,
          );
        } else {
          console.log(
            `[BIO-TC][${dominio}] resp.success=`,
            resp?.success,
            "offline=",
            resp?.offline,
          );
        }

        if (!resp?.success || !resp?.done) return;

        const done = resp.done;

        // Monta o patch SOMENTE com os campos do domínio.
        const buildPatch = (prev: any): any => {
          const base = prev || {};
          if (dominio === "ronda") {
            return {
              ...base,
              ronda: done.ronda ?? [],
              rondaHistorico: done.rondaHistorico ?? [],
            };
          }
          if (dominio === "livro") {
            // Carga TOTALMENTE vazia é indistinguível de falha silenciosa do backend —
            // preserva o snapshot anterior em vez de zerar o disco (mesmo tratamento
            // dado a clientes/equipes logo abaixo). Sem isto, um erro engolido no
            // /carregar apagava as listas de colaboradores do Livro até a próxima carga
            // boa, sem nenhuma mensagem.
            const vazioTotal =
              (done.livroOcorrencia?.length ?? 0) === 0 &&
              (done.livroOcorrenciaItensTipos?.length ?? 0) === 0 &&
              done.livroOcorrenciaParametros == null;
            if (vazioTotal && base.livroOcorrenciaParametros != null) {
              return base;
            }
            return {
              ...base,
              livroOcorrencia: done.livroOcorrencia ?? [],
              livroOcorrenciaItensTipos: done.livroOcorrenciaItensTipos ?? [],
              livroOcorrenciaParametros: done.livroOcorrenciaParametros ?? null,
            };
          }
          return {
            ...base,
            ordemServicos: done.ordemServicos ?? [],
            // Listas para o filtro / OS sob demanda (clientes ativos + equipes ativas do tenant),
            // vindas do endpoint de OS. Preserva o valor anterior se o endpoint não as trouxer.
            clientes: done.clientes ?? base.clientes ?? [],
            equipes: done.equipes ?? base.equipes ?? [],
          };
        };

        // Merge funcional sobre o estado MAIS RECENTE (via ref), nunca closure obsoleto.
        let merged = buildPatch(userDataRef.current);

        // OS: reaplica patches locais (sob demanda + status) para não perder alterações offline.
        if (dominio === "os") {
          merged = await SyncService.applyLocalOsSobDemanda(merged);
          merged = await SyncService.applyLocalOsStatuses(merged);
        }

        // GUARD DE SESSÃO: signOut/trocarEmpresa no meio do fetch → descarta tudo.
        // Persistir aqui ressuscitaria/contaminaria o userData da sessão atual com
        // dados da anterior (userDataRef já pode estar nulo → merged seria um userData
        // só com este domínio). Também NÃO carimba o throttle: o signOut limpou as
        // chaves, e recarimbá-las deixaria a sessão nova 24h SEM este domínio.
        if (peekApiSessionGen() !== sessionGen) return;
        // Persiste e atualiza o estado (replica o setUserData).
        userDataRef.current = merged;
        await writeJSONFile("userData.json", merged);
        // signOut no meio da gravação: não regrava o estado nem carimba o throttle
        // (o arquivo não fica sujo — o deleteJSONFile do signOut entrou na fila de
        // I/O depois desta escrita; o userDataRef é ressincronizado pelo efeito de
        // espelhamento no próximo render).
        if (peekApiSessionGen() !== sessionGen) return;
        setUserDataState(merged);

        await tcMarkFetched(key);
      } catch (error) {
        console.warn(
          `[Auth] Falha ao carregar domínio t-cred '${dominio}':`,
          error,
        );
      }
    },
    [resolveIdColaborador, tcShouldFetch, tcMarkFetched],
  );

  const refreshUserData = useCallback(async () => {
    // Geração de sessão NO INÍCIO da chamada (síncrono): qualquer signOut/signIn no
    // meio do fluxo muda a geração e ABORTA as gravações abaixo (ver GERAÇÃO DE SESSÃO).
    const sessionGen = peekApiSessionGen();
    const sessaoAtiva = () => peekApiSessionGen() === sessionGen;

    // Já há uma atualização em andamento NA MESMA SESSÃO: devolve a MESMA promise
    // (espera o fetch real concluir) em vez do userData atual, que pode estar
    // nulo/obsoleto. Uma promise de sessão ANTERIOR não serve — ela vai abortar sem
    // gravar; entregá-la à sessão nova deixaria o pós-relogin sem o primeiro fetch.
    if (
      refreshUserDataInFlightRef.current &&
      refreshUserDataPromiseRef.current &&
      refreshUserDataSessionGenRef.current === sessionGen
    ) {
      return refreshUserDataPromiseRef.current;
    }

    refreshUserDataInFlightRef.current = true;
    refreshUserDataSessionGenRef.current = sessionGen;

    // Carga do perfil SEM nada na tela (ver CargaPerfil): só esta situação mexe no
    // estado — com userData em tela, uma falha do /iniciar segue invisível (offline-first).
    let acompanharCarga = false;
    let perfilCarregado = false;
    let falhaCarga: Pick<CargaPerfil, "motivo" | "mensagem"> = {
      motivo: "conexao",
      mensagem: null,
    };

    const run = (async () => {
      try {
        const cachedUserDataBruto = await readJSONFile("userData.json");
        // 🔒 CROSS-TENANT (30/07): o userData.json é ÚNICO no aparelho. Se ele é
        // de OUTRO tenant (trocou de empresa e o app reiniciou / o /iniciar novo
        // ainda não respondeu), adotá-lo mostraria documentos, notificações e
        // espelho de ponto da empresa ANTERIOR sob a sessão atual — e os
        // applyLocal* (escopados no tenant CERTO) mesclariam por cima. Descarta:
        // melhor tela vazia por 1 request do que dado da outra empresa.
        const cachedUserData = await (async () => {
          if (!cachedUserDataBruto) return cachedUserDataBruto;
          const hArq = String(
            (cachedUserDataBruto as any)?.hash ??
              (cachedUserDataBruto as any)?.done?.hash ??
              "",
          )
            .trim()
            .toUpperCase();
          const hSessao = await tenantHashDaSessao().catch(() => "");
          if (hArq && hSessao && hArq !== hSessao) {
            console.warn(
              "[Auth] 🔒 userData.json em cache é de OUTRO tenant — descartado (aguardando /iniciar da empresa atual).",
            );
            return null;
          }
          return cachedUserDataBruto;
        })();

        if (cachedUserData && !userDataRef.current && sessaoAtiva()) {
          setUserDataState(cachedUserData);
        }

        const authToken = tokenRef.current?.trim() || null;
        if (!authToken) {
          return cachedUserData;
        }

        acompanharCarga = !userDataRef.current && !cachedUserData;
        if (acompanharCarga && sessaoAtiva()) {
          // Mantém `falhou` de uma tentativa anterior: o aviso continua na tela, com o
          // botão em "Tentando…", em vez de sumir e voltar.
          setCargaPerfil((c) => (c.tentando ? c : { ...c, tentando: true }));
        }

        let baseData = cachedUserData;
        let iniciarFresco = false;

        try {
          const response = await Api.start.iniciar();

          if (!(response.success && response.done)) {
            // `offline` com corpo JSON = quem respondeu foi o servidor (503, proxy). Sem
            // corpo (rede caiu, tempo esgotado) ou com página HTML (portal de Wi-Fi, rede que
            // bloqueia) = o problema está no caminho até ele.
            falhaCarga = {
              motivo: !response.offline
                ? "recusa"
                : response.done != null && typeof response.done === "object"
                  ? "servidor"
                  : "conexao",
              mensagem: response.offline ? null : (response.erros?.[0] ?? null),
            };
          }

          if (response.success && response.done) {
            // Carimbo de FRESCOR (por tenant): um /iniciar REAL respondeu agora. É o que
            // permite ao resolver de documentos confirmar revert de assinatura
            // (docAssinadoLog); hidratação de CACHE nunca carimba — dado velho não cobra
            // falso pendente. Aguardado: a conferência de batidas lá embaixo lê o carimbo.
            await gravarUserDataFetchedAt(response.done?.hash);
            iniciarFresco = true;
            baseData = response.done;
            // ronda/livro/OS NÃO vêm mais no /iniciar (são carregados por endpoints
            // dedicados). PRESERVA os valores já carregados — nunca deixa o /iniciar
            // zerá-los. Garante o offline-first: o último dado é sempre mantido.
            // 🔒 CROSS-TENANT (30/07): só preserva quando o snapshot anterior é do
            // MESMO tenant do payload fresco. No trocarEmpresa (signIn SEM signOut)
            // o userDataRef ainda é o da empresa ANTERIOR — preservar ali copiava
            // ronda/livro/OS/equipes/clientes da empresa A para dentro do userData
            // da B (e persistia no userData.json). Tenant divergente → usa só o
            // payload fresco; os domínios de B chegam pelo carregarDominioTcred.
            const prevBruto = userDataRef.current ?? cachedUserData;
            const hashDe = (u: any) =>
              String(u?.hash ?? u?.done?.hash ?? "")
                .trim()
                .toUpperCase();
            const hashFresco = hashDe(baseData);
            const hashPrev = hashDe(prevBruto);
            const mesmoTenant =
              !hashFresco || !hashPrev || hashFresco === hashPrev;
            if (!mesmoTenant) {
              console.warn(
                "[Auth] 🔒 userData anterior é de OUTRO tenant — domínios NÃO preservados (trocarEmpresa).",
              );
            }
            const prev = mesmoTenant ? prevBruto : null;
            if (prev) {
              baseData = {
                ...baseData,
                ronda: prev.ronda ?? baseData.ronda,
                rondaHistorico: prev.rondaHistorico ?? baseData.rondaHistorico,
                ordemServicos: prev.ordemServicos ?? baseData.ordemServicos,
                // clientes (postos) AGORA vêm do /iniciar: usa a lista fresca quando vier
                // preenchida; senão preserva a anterior (offline-first). Antes era sempre
                // preservada porque o /iniciar não trazia clientes (vinha só do domínio de OS).
                clientes:
                  Array.isArray(baseData.clientes) &&
                  baseData.clientes.length > 0
                    ? baseData.clientes
                    : (prev.clientes ?? baseData.clientes),
                // equipes continuam vindo só do domínio de OS → preserva o anterior.
                equipes: prev.equipes ?? baseData.equipes,
                livroOcorrencia:
                  prev.livroOcorrencia ?? baseData.livroOcorrencia,
                // Mesmo tratamento de `clientes` acima, e pelo MESMO motivo. O `??` sozinho
                // não servia aqui: o domínio "livro" grava este campo com `?? []`, então ele
                // é SEMPRE um array — nunca null/undefined. Com isso o `??` nunca caía para o
                // baseData, e bastava uma carga chegar vazia (livro ainda não carregado, ou
                // carregado antes de o usuário ter livro) para a lista de tipos ficar presa em
                // [] até um login novo. Era isso que deixava o select de "tipo do item" vazio.
                livroOcorrenciaItensTipos:
                  Array.isArray(baseData.livroOcorrenciaItensTipos) &&
                  baseData.livroOcorrenciaItensTipos.length > 0
                    ? baseData.livroOcorrenciaItensTipos
                    : (prev.livroOcorrenciaItensTipos ??
                      baseData.livroOcorrenciaItensTipos),
                livroOcorrenciaParametros:
                  prev.livroOcorrenciaParametros ??
                  baseData.livroOcorrenciaParametros,
              };
            }
            // Atualiza dados estáticos (postos, veículos, pessoas, checklists) em paralelo ao iniciar
            void refreshStaticData();
          }
        } catch (error) {
          console.error("Erro ao atualizar userData:", error);
        }

        // SEMPRE mescla pontos locais, status de OS e status de documentos locais, mesmo se a API falhar ou estiver offline
        if (baseData) {
          let merged = await SyncService.applyLocalPontos(baseData);
          merged = await SyncService.applyLocalOsSobDemanda(merged);
          merged = await SyncService.applyLocalOsStatuses(merged);
          merged = await SyncService.applyLocalDocStatus(merged);
          merged = await SyncService.applyLocalNotificacoesLidas(merged);
          merged = await SyncService.applyLocalEnquetes(merged);

          // GUARD DE SESSÃO (a RAIZ do logoff espúrio no relogin): se um signOut —
          // remoto, homolog ou manual — aconteceu enquanto o /iniciar + merges rodavam,
          // este merged pertence à sessão MORTA. Regravá-lo "ressuscitaria" o
          // userData.json (ex.: forcar_logoff='S' capturado antes do rearme) e, num
          // trocarEmpresa, vazaria os dados da empresa anterior por cima da nova.
          if (!sessaoAtiva()) {
            console.warn(
              "[Auth] refreshUserData DESCARTADO: sessão encerrada/trocada durante a atualização.",
            );
            return null;
          }
          await setUserData(merged);
          // setUserData re-checa a geração após a gravação; se a sessão morreu nesse
          // meio-tempo, também não dispara os domínios abaixo nem devolve dado morto.
          if (!sessaoAtiva()) {
            return null;
          }
          perfilCarregado = !!userDataRef.current;

          // userData base já setado (instant-load preservado). DEPOIS disso, dispara em
          // background o carregamento dos 3 domínios do t-credentials, cada um com o
          // throttle de 24h por colaborador. Não bloqueia e nunca lança (best-effort).
          void carregarDominioTcred("ronda");
          void carregarDominioTcred("livro");
          void carregarDominioTcred("os");

          // /iniciar REAL acabou de ser gravado: com o cache fresco, um dreno faz a cura
          // retroativa achar batida "enviada" que não chegou ao banco — sem depender de
          // alguém puxar a tela de Justificativas. Só roda se houver batida a conferir.
          if (iniciarFresco) void SyncService.conferirPontosAposIniciar();

          return merged;
        }

        return baseData;
      } finally {
        // Estado da carga do perfil — no finally para que nenhum erro deixe o botão
        // preso em "Tentando…". Sessão trocada no meio: a sessão nova cuida do dela.
        if (sessaoAtiva()) {
          if (perfilCarregado) {
            setCargaPerfil((c) =>
              c === CARGA_PERFIL_INICIAL ? c : CARGA_PERFIL_INICIAL,
            );
          } else if (acompanharCarga && !userDataRef.current) {
            setCargaPerfil({ tentando: false, falhou: true, ...falhaCarga });
          } else if (acompanharCarga) {
            setCargaPerfil((c) => (c.tentando ? { ...c, tentando: false } : c));
          }
        }
        // Só limpa os refs se esta execução ainda for a "dona" deles — uma chamada de
        // sessão NOVA pode tê-los assumido enquanto esta (de sessão morta) terminava;
        // limpar aqui derrubaria o dedup do fetch legítimo em voo.
        if (refreshUserDataSessionGenRef.current === sessionGen) {
          refreshUserDataInFlightRef.current = false;
          refreshUserDataPromiseRef.current = null;
        }
      }
    })();

    refreshUserDataPromiseRef.current = run;
    return run;
  }, [setUserData, carregarDominioTcred]);

  const refreshStaticData = useCallback(async () => {
    // tokenRef, não o estado: quem chama é o refreshUserData, que pode vir da closure de
    // um render anterior ao signIn (ver tokenRef).
    if (refreshStaticDataInFlightRef.current || !tokenRef.current) {
      return;
    }

    refreshStaticDataInFlightRef.current = true;
    // Ver GERAÇÃO DE SESSÃO: fetches longos em background — nada persiste se a sessão mudar.
    const sessionGen = peekApiSessionGen();

    try {
      const [postosRes, veiculosRes, pessoasRes, checklistsRes] =
        await Promise.all([
          Api.start.listaTodosPostos(),
          Api.start.listaTodosVeiculos(),
          Api.start.listaTodasPessoas(),
          Api.geral.obterChecklists(),
        ]);

      // GUARD DE SESSÃO: signOut/trocarEmpresa durante os fetches → descarta tudo.
      // Persistir aqui regravaria as listas (postos/veículos/pessoas/checklists) da
      // sessão morta por cima da limpeza do signOut — ou por cima da sessão nova.
      if (peekApiSessionGen() !== sessionGen) {
        return;
      }

      const promises: Promise<unknown>[] = [];

      // Em ARQUIVO (services/CacheListas.ts), nunca no AsyncStorage: a lista de uma empresa
      // grande passa de ~2 MB e, no Android, não volta mais da leitura.
      if (postosRes.success && postosRes.done) {
        setPostos(postosRes.done);
        promises.push(gravarListaCache("postos", postosRes.done, sessionGen));
      }

      if (veiculosRes.success && veiculosRes.done) {
        setVeiculos(veiculosRes.done);
        promises.push(gravarListaCache("veiculos", veiculosRes.done, sessionGen));
      }

      if (pessoasRes.success && pessoasRes.done) {
        setPessoas(pessoasRes.done);
        promises.push(gravarListaCache("pessoas", pessoasRes.done, sessionGen));
      }

      if (checklistsRes.success && checklistsRes.done) {
        setChecklists(checklistsRes.done);
        promises.push(gravarListaCache("checklists", checklistsRes.done, sessionGen));
      }

      if (promises.length > 0) {
        await Promise.all(promises);
      }
    } catch (error) {
      console.error("Erro ao atualizar dados estáticos:", error);
    } finally {
      refreshStaticDataInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isHydrated || !token || userData) {
      return;
    }

    void refreshUserData();
  }, [isHydrated, refreshUserData, token, userData]);

  // Catálogo CID-10 do atestado médico — garantido NO BOOT, sempre que há token.
  //
  // ⚠️ Não vive no `refreshStaticData` de propósito: aquele só roda a partir do
  // `refreshUserData`, que no boot é pulado quando o `userData` volta do cache — o
  // caso de TODO mundo que já está logado e só atualizou o app pela loja. Aqui o
  // gatilho é o token, então cobre login novo, sessão restaurada e atualização.
  // Barato no caso comum (um `getInfoAsync`); só baixa quando não há arquivo.
  useEffect(() => {
    if (!isHydrated || !token) return;
    void garantirCatalogoCidNoAparelho();
  }, [isHydrated, token]);

  // Enrollment biométrico em background: gera embedding local da foto cadastrada
  // pra que a verificação no ponto rode 100% offline.
  useEffect(() => {
    if (!userData) return;

    const sources = [userData, userData?.done, userData?.colaborador].filter(
      Boolean,
    );
    const pick = (key: string) => {
      for (const s of sources) {
        const v = s?.[key];
        if (v != null && String(v).trim() !== "") return v;
      }
      return null;
    };

    // Se o usuário JÁ cadastrou a própria biometria no BiometricSetup, a captura dele é a
    // referência autoritativa (e a foto do preview em MeusDados). NÃO mexe na referência:
    // nem limpa (mesmo com biometria='N'), nem sobrescreve com a foto do servidor.
    // ESTE return precisa vir ANTES do clearReference abaixo — senão o cadastro local é
    // apagado logo após o BiometricSetup (quando biometria='N', i.e. permite_sem_biometria='S').
    if (isBiometricsSetupDone) return;

    const biometriaFlag = String(pick("biometria") ?? "")
      .trim()
      .toUpperCase();
    if (biometriaFlag === "N") {
      // Colaborador não usa biometria E não cadastrou localmente — limpa referência antiga.
      void FaceRecognition.clearReference();
      return;
    }

    const fotoBiometria = pick("foto_biometria");
    if (typeof fotoBiometria !== "string" || fotoBiometria.length < 100) return;

    let cancelled = false;
    void (async () => {
      try {
        const result = await FaceRecognition.enrollFromBase64(fotoBiometria);
        if (cancelled) return;
        if (result.enrolled) {
          console.log("Biometria facial cadastrada localmente.");
        } else if (!result.skipped) {
          console.warn(
            "Não foi possível cadastrar biometria local:",
            result.reason,
          );
        }
      } catch (err) {
        if (!cancelled) console.warn("Falha no enrollment biométrico:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userData, isBiometricsSetupDone]);

  const signIn = useCallback(
    async (nextToken: string, nextId?: string) => {
      // WEB: entrou de novo — o Voltar/endereço sem sessão deixa de mandar para o login do
      // TirvuAdm (marca do "Sair", platform/saida-do-app).
      esquecerSaidaParaLogin();
      // SESSÃO NOVA: invalida (síncrono, antes de qualquer await) toda persistência
      // assíncrona em voo da sessão anterior — cobre o trocarEmpresa, que faz signIn
      // SEM signOut (ver GERAÇÃO DE SESSÃO). O token de API também troca JÁ: uma request
      // enfileirada durante as gravações abaixo deve sair com o Bearer novo.
      bumpApiSessionGen();
      setApiAuthToken(nextToken);
      setCargaPerfil(CARGA_PERFIL_INICIAL);

      // 🔒 TENANT MUDOU → ZERA o userData JÁ (síncrono, antes de qualquer await).
      // Sem isto, todo componente que lê `userData` (resumos da Home, header,
      // badges…) continuava pintando os dados da empresa ANTERIOR pelos ~2-3s até
      // o /iniciar da nova responder — e não há skeleton que cubra tudo. Zerando,
      // as telas caem no estado "sem dados" (skeleton/vazio) e só voltam com o
      // payload da empresa CERTA: nunca se vê dado de um tenant em outro.
      // O hash novo vem do claim c_hash do token que acabou de ser setado; o
      // arquivo também é apagado para bootstrap/refresh não o ressuscitarem.
      // Relogin na MESMA empresa (hash igual) preserva o cache — nada pisca lá.
      try {
        const hashNovo = await tenantHashDaSessao().catch(() => "");
        const hashAtual = String(
          (userDataRef.current as any)?.hash ??
            (userDataRef.current as any)?.done?.hash ??
            "",
        )
          .trim()
          .toUpperCase();
        if (hashNovo && hashAtual && hashNovo !== hashAtual) {
          console.warn(
            "[Auth] 🔒 Troca de empresa detectada no signIn — limpando userData da empresa anterior.",
          );
          userDataRef.current = null;
          setUserDataState(null);
          await deleteJSONFile("userData.json").catch(() => {});
        }
      } catch {}

      const promises: Promise<any>[] = [
        AsyncStorage.setItem(AUTH_TOKEN_STORAGE_KEY, nextToken),
      ];
      if (nextId) {
        promises.push(AsyncStorage.setItem(AUTH_ID_STORAGE_KEY, nextId));
      }

      // 🔒 CROSS-TENANT no trocarEmpresa (signIn sem signOut): os CATÁLOGOS
      // espelhados (postos/veículos/pessoas/checklists) e o THROTTLE de domínios
      // (tc_lastfetch) pertencem ao tenant ANTERIOR — ids colidem entre bancos.
      // Sem esta limpeza: (a) a Ronda/OS abria checklist/posto da empresa antiga
      // até o refreshStaticData completar (e após restart do app, o boot
      // re-hidratava a lista errada); (b) a colisão de idColaborador no throttle
      // SUPRIMIA por 24h o fetch de ronda/livro/OS no tenant novo, mantendo os
      // domínios da empresa anterior dentro do userData.
      promises.push(tcClearAllThrottle());
      promises.push(apagarListasCache());
      setPostos(null);
      setVeiculos(null);
      setPessoas(null);
      setChecklists(null);
      setBancos(null);

      await Promise.all(promises);

      tokenRef.current = nextToken;
      setToken(nextToken);
      if (nextId) {
        setIdColaborador(nextId);
      }
    },
    [tcClearAllThrottle],
  );

  /**
   * Volta do SEGUNDO PLANO → /iniciar de novo (17/09/2026).
   *
   * O app fica vivo por dias em segundo plano e NADA refazia o /iniciar ao voltar: o
   * `userData` era o snapshot da última navegação pela Home (ou do login). Sintoma
   * relatado: ponto seguia "bloqueado por lançamento" dias depois do fim do lançamento,
   * até sair e logar. Aqui, ao ficar `active`, atualiza — com um espaçamento mínimo de
   * 2 min (a troca rápida app ↔ câmera ↔ app não vira rajada; o t-credentials ainda
   * tem cache de 3 min por colaborador, então o custo no servidor é ~zero).
   * `refreshUserData` já é deduplicado em voo e, offline, cai no cache local.
   */
  const refreshUserDataRef = useRef(refreshUserData);
  refreshUserDataRef.current = refreshUserData;
  const ultimoRefreshForegroundRef = useRef(0);
  useEffect(() => {
    if (!isHydrated || !token) return;
    const sub = AppState.addEventListener("change", (state: string) => {
      if (state !== "active") return;
      const agora = Date.now();
      if (agora - ultimoRefreshForegroundRef.current < 2 * 60_000) return;
      ultimoRefreshForegroundRef.current = agora;
      void refreshUserDataRef.current().catch(() => {});
    });
    return () => sub.remove();
  }, [isHydrated, token]);

  const markBiometricsSetupDone = useCallback(async () => {
    try {
      await AsyncStorage.setItem(AUTH_BIOMETRICS_SETUP_KEY, "true");
      setIsBiometricsSetupDone(true);
    } catch (error) {
      console.error("Erro ao marcar biometria como configurada:", error);
    }
  }, []);

  // ── Logout FORÇADO de sessão de HOMOLOGAÇÃO (2026-07-11) ─────────────────────
  // Usuários que ficaram logados em banco de homologação (hash REALJGHOMOLOGACAO /
  // AGILHOMOLOGACAO / SOLLHOMOLOGACAO etc.) precisam ser direcionados ao banco real.
  // Regra: hash da sessão casa /HOMOLO[GC]/ (mesma convenção do filtro do
  // EmpresaSwitcher, que cobre a grafia "HOMOLOCACAO") → best-effort de drenar as
  // pendências pro ambiente ANTIGO → LIMPA o estado local de sync (fila/mapas
  // sobrevivem ao signOut e contaminariam o tenant real com ids do banco errado) →
  // signOut + aviso. No relogin, o listarEmpresas JÁ FILTRA empresas de homologação →
  // o usuário só consegue entrar no ambiente correto.
  const homologLogoutRef = useRef(false);
  useEffect(() => {
    if (!isHydrated || !token || !userData) return;
    const hashSessao = String(
      (userData as any)?.hash ?? (userData as any)?.done?.hash ?? "",
    )
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();
    if (!/HOMOLO[GC]/.test(hashSessao)) return;
    if (homologLogoutRef.current) return;
    homologLogoutRef.current = true;
    void (async () => {
      console.warn(
        "[Auth] Sessão de HOMOLOGAÇÃO detectada (hash=" +
          hashSessao +
          ") — forçando logout.",
      );
      try {
        await SyncService.syncAll(); // entrega o que der ao ambiente antigo
      } catch {}
      try {
        // Passa o hash da sessão de homologação: o wipe descarta SÓ o que é
        // desta sessão e PRESERVA ações/pontos retidos de outros tenants
        // (ex.: batidas de produção aguardando a empresa dona).
        await SyncService.limparEstadoLocalSync(hashSessao);
      } catch {}
      await signOut();
      // Mesmo destino do logout normal (confirmLogout) — sem isso o usuário ficaria
      // numa Home sem sessão.
      router.replace("/environment-select");
      Alert.alert(
        T("Acesso atualizado"),
        T("Seu acesso estava no ambiente de TESTES (homologação) e foi encerrado. Entre novamente para acessar o ambiente correto da sua empresa."),
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated, token, userData]);

  // ── Logoff REMOTO comandado pelo servidor (2026-07-12) ───────────────────────
  // O admin seta tab_adm_colaboradores.forcar_logoff='S' e o /iniciar entrega o
  // campo ao app (SELECT A.* → raiz do userData). Fluxo COOPERATIVO — jamais matar
  // o token no servidor: as pendências offline seriam rejeitadas (401) e perderíamos
  // ponto. Aqui: drena a fila → se AINDA houver pendência, ADIA (a flag continua no
  // servidor e o próximo sync tenta de novo) → confirma ao t-credentials
  // (logoffExecutado limpa a flag; o /login também rearma, como cinto de segurança)
  // → signOut SEM limpar o estado de sync (tenant de produção: mapa/logs ficam; os
  // guards por hash já isolam tenants). Supressão de 5min pós-execução: réplicas do
  // /iniciar cacheiam ~45s — sem ela, um relogin rápido leria a flag VELHA e seria
  // derrubado em loop.
  const LOGOFF_REMOTO_EM_KEY = "@TirvuMais:logoff_remoto_em";
  const LOGOFF_REMOTO_SUPRESSAO_MS = 5 * 60 * 1000;
  const logoffRemotoRef = useRef(false);
  // Modal estilizado (mesmo padrão dos avisos do Registro.tsx) — o provider fica
  // montado através da navegação, então o aviso aparece POR CIMA da tela de login
  // após o router.replace (Alert nativo destoava do resto do app).
  const [logoffRemotoAviso, setLogoffRemotoAviso] = useState(false);
  useEffect(() => {
    if (!isHydrated || !token || !userData) return;
    const hashSessao = String(
      (userData as any)?.hash ?? (userData as any)?.done?.hash ?? "",
    )
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();
    if (/HOMOLO[GC]/.test(hashSessao)) return; // homolog tem fluxo próprio (acima)
    const flag = (() => {
      const sources = [
        userData,
        (userData as any)?.done,
        (userData as any)?.colaborador,
      ].filter(Boolean);
      for (const s of sources) {
        const v = (s as any)?.forcar_logoff ?? (s as any)?.forcarLogoff;
        if (v != null && String(v).trim() !== "")
          return String(v).trim().toUpperCase();
      }
      return "";
    })();
    if (flag !== "S") return;
    // O ref é guarda de VOO EM ANDAMENTO apenas — o AuthProvider vive pelo processo
    // inteiro do app, então ele SEMPRE volta a false no finally. (Bug corrigido em
    // 2026-07-12: ficava true após um logoff bem-sucedido e bloqueava qualquer
    // comando futuro na mesma execução do app — 2º logoff nunca disparava.)
    if (logoffRemotoRef.current) return;
    logoffRemotoRef.current = true;
    void (async () => {
      try {
        try {
          const ultimoMs = Number(
            (await AsyncStorage.getItem(LOGOFF_REMOTO_EM_KEY)) || 0,
          );
          if (
            ultimoMs > 0 &&
            Date.now() - ultimoMs < LOGOFF_REMOTO_SUPRESSAO_MS
          ) {
            // Flag 'S' de cache velho de réplica logo após o relogin — ignora.
            return;
          }
        } catch {}
        console.warn(
          "[Auth] Logoff REMOTO comandado pelo servidor — drenando pendências antes de sair.",
        );
        try {
          await SyncService.syncAll(); // best-effort: entrega tudo que der
        } catch {}
        // RE-VALIDA a flag no dado FRESCO pós-drenagem (o syncAll refez o /iniciar e
        // regravou o userData.json): o disparo pode ter vindo de userData VELHO —
        // hidratação do cache no boot, ou um refreshUserData em voo que "ressuscitou"
        // a flag 'S' depois de um logoff anterior. Sem isto, o usuário relogado era
        // derrubado UMA vez à toa mesmo com a flag já 'N' no servidor.
        try {
          const fresco: any = await readJSONFile("userData.json");
          const fontes = [fresco, fresco?.done, fresco?.colaborador].filter(
            Boolean,
          );
          let flagFresca = "";
          for (const s of fontes) {
            const v = (s as any)?.forcar_logoff ?? (s as any)?.forcarLogoff;
            if (v != null && String(v).trim() !== "") {
              flagFresca = String(v).trim().toUpperCase();
              break;
            }
          }
          if (flagFresca !== "S") {
            console.warn(
              "[Auth] Logoff remoto CANCELADO: flag não é mais 'S' no dado fresco (disparo era cache velho).",
            );
            return;
          }
        } catch {
          // sem leitura fresca (I/O) → segue com o disparo original (conservador:
          // executar o comando do admin é o comportamento esperado)
        }
        try {
          const pend = await SyncService.contarPendenciasSync();
          if (pend.total > 0 || pend.indeterminado) {
            // NUNCA derruba dado: adia; a flag segue no servidor e o próximo
            // /iniciar dispara nova tentativa. `indeterminado` = falha ao LER o
            // estado local (contagem não confiável) — sem prova de fila vazia,
            // também adia (perder pendência é pior que atrasar o comando).
            console.warn(
              pend.indeterminado
                ? "[Auth] Logoff remoto ADIADO: pendências de sync INDETERMINADAS (falha ao ler o estado local)."
                : `[Auth] Logoff remoto ADIADO: ${pend.total} pendência(s) de sync ainda não entregues.`,
            );
            return;
          }
        } catch {
          // Mesma regra do indeterminado: sem contagem confiável, adia.
          console.warn(
            "[Auth] Logoff remoto ADIADO: erro ao contar pendências de sync.",
          );
          return;
        }
        try {
          await AsyncStorage.setItem(LOGOFF_REMOTO_EM_KEY, String(Date.now()));
        } catch {}
        try {
          await Api.autenticacao.confirmarLogoffRemoto(); // antes do signOut (Bearer ainda vale)
        } catch {}
        try {
          await signOut();
        } catch (e) {
          // signOut pode rejeitar em falha de I/O nativa (deleteJSONFile relança).
          // Mesmo meio-deslogado, o destino certo é a tela de login — segue o fluxo.
          console.warn("[Auth] Logoff remoto: signOut falhou parcialmente:", e);
        }
        router.replace("/environment-select");
        setLogoffRemotoAviso(true);
      } finally {
        // Sempre rearma: pós-signOut o userData é null (efeito não re-dispara), e o
        // relogin com flag velha de cache é coberto pela janela de supressão acima.
        logoffRemotoRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated, token, userData]);

  const signOut = useCallback(async () => {
    // SESSÃO ENCERRADA: invalida (síncrono, antes de qualquer await) toda persistência
    // assíncrona em voo — um refreshUserData/carregarDominioTcred/syncAll que termine
    // depois deste ponto NÃO pode regravar o userData da sessão morta (ressuscitaria
    // flags como forcar_logoff='S' capturadas antes do rearme). O token de API é limpo
    // AQUI (e não após os removes): request pós-signOut não deve sair com Bearer morto.
    bumpApiSessionGen();
    clearApiAuthToken();
    // Já aqui, e não depois dos removes: um refreshUserData que comece nesta janela pega a
    // geração NOVA — com o token ainda no ref, ele buscaria e gravaria a sessão morta.
    tokenRef.current = null;
    setCargaPerfil(CARGA_PERFIL_INICIAL);
    await Promise.all([
      AsyncStorage.removeItem(AUTH_TOKEN_STORAGE_KEY),
      AsyncStorage.removeItem(AUTH_ID_STORAGE_KEY),
      AsyncStorage.removeItem(AUTH_USER_DATA_KEY),
      apagarListasCache(),
      AsyncStorage.removeItem(AUTH_BIOMETRICS_SETUP_KEY),
      AsyncStorage.removeItem(AUTH_FCM_TOKEN_KEY),
      deleteJSONFile("userData.json"),
      FaceRecognition.clearReference(),
      tcClearAllThrottle(),
    ]);
    setToken(null);
    setIdColaborador(null);
    setUserDataState(null);
    setPostos(null);
    setVeiculos(null);
    setPessoas(null);
    setChecklists(null);
    setBancos(null);
    setIsBiometricsSetupDone(false);
    setFcmTokenState(null);
  }, [tcClearAllThrottle]);

  // Política da empresa: permite bater ponto SEM biometria? ('S' = NÃO obrigatório
  // a cadastrar). Mesmo algoritmo de leitura usado em Camera.tsx (varre userData,
  // userData.done e userData.colaborador).
  const permiteSemBiometria = useMemo(() => {
    const sources = [userData, userData?.done, userData?.colaborador].filter(
      Boolean,
    );
    for (const s of sources) {
      const v =
        (s as any)?.permiteSemBiometria ?? (s as any)?.permite_sem_biometria;
      if (v != null && String(v).trim() !== "")
        return String(v).trim().toUpperCase();
    }
    return "";
  }, [userData]);

  /**
   * Biometria que JÁ EXISTE no servidor (12/09/2026): quando a empresa exige biometria
   * (`permite_sem_biometria = 'N'`) e este aparelho ainda não tem cadastro, pergunta ao
   * t-credentials pela face do próprio colaborador (coletor, TirvuAdm, outro celular) e
   * a registra localmente — em vez de exigir um cadastro novo. Roda no BOOT com sessão,
   * que é o que cobre quem já estava logado e só atualizou o app; o login novo faz o
   * mesmo ANTES de decidir o destino (Onboarding.navegarAposLogin).
   *
   * Uma tentativa por colaborador por sessão do processo: 'falhou' (sem rede) tenta de
   * novo na próxima abertura; 'sem_cadastro' não insiste — o caminho é o cadastro.
   */
  const biometriaServidorTentadaRef = useRef<string>("");
  useEffect(() => {
    if (!isHydrated || !token || !userData) return;
    if (isBiometricsSetupDone || permiteSemBiometria !== "N") return;
    const chave = `${idColaborador ?? ""}|${token.slice(-12)}`;
    if (biometriaServidorTentadaRef.current === chave) return;
    biometriaServidorTentadaRef.current = chave;
    let cancelado = false;
    void (async () => {
      try {
        if (await FaceRecognition.hasReference()) {
          // Referência local existe (ex.: cadastro feito pelo fluxo do ponto) sem o
          // selo do setup — a exigência já está satisfeita.
          if (!cancelado) await markBiometricsSetupDone();
          return;
        }
        const r = await FaceBiometria.importarDoServidor();
        if (cancelado) return;
        if (r === "importado") {
          await markBiometricsSetupDone();
          console.log(
            "[Auth] Biometria do servidor registrada; cadastro dispensado.",
          );
        } else if (r === "falhou") {
          biometriaServidorTentadaRef.current = ""; // tenta de novo na próxima
        }
      } catch (e) {
        console.warn("[Auth] importação da biometria do servidor:", e);
        biometriaServidorTentadaRef.current = "";
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [
    isHydrated,
    token,
    userData,
    idColaborador,
    isBiometricsSetupDone,
    permiteSemBiometria,
    markBiometricsSetupDone,
  ]);

  const value = useMemo(
    () => ({
      token,
      idColaborador,
      userData,
      cargaPerfil,
      postos,
      veiculos,
      pessoas,
      checklists,
      bancos,
      location,
      isAuthenticated: !!token?.trim(),
      isHydrated,
      // Se a empresa permite ponto sem biometria (permiteSemBiometria === 'S'), o
      // usuário NÃO é obrigado a cadastrar — então o "gate" de setup é considerado
      // satisfeito e os redirects automáticos para /biometric-setup são pulados
      // (Onboarding e Home). Não afeta o cadastro manual em "Meus Dados".
      isBiometricsSetupDone:
        isBiometricsSetupDone || permiteSemBiometria === "S",
      fcmToken,
      signIn,
      signOut,
      setUserData,
      refreshUserData,
      refreshStaticData,
      carregarDominioTcred,
      ensureBancosLoaded,
      ensureChecklistsLoaded,
      refreshLocation,
      markBiometricsSetupDone,
      setFcmToken,
      syncFirebaseToken,
      clearAuth: async () => {
        // Mesma regra do signOut: bump síncrono + token de API limpo JÁ (ver GERAÇÃO DE SESSÃO).
        bumpApiSessionGen();
        clearApiAuthToken();
        tokenRef.current = null;
        setCargaPerfil(CARGA_PERFIL_INICIAL);
        await Promise.all([
          AsyncStorage.removeItem(AUTH_TOKEN_STORAGE_KEY),
          AsyncStorage.removeItem(AUTH_ID_STORAGE_KEY),
          AsyncStorage.removeItem(AUTH_USER_DATA_KEY),
          deleteJSONFile("userData.json"),
          tcClearAllThrottle(),
          apagarListasCache(),
        ]);
        setToken(null);
        setIdColaborador(null);
        setUserDataState(null);
        setPostos(null);
        setVeiculos(null);
        setPessoas(null);
        setChecklists(null);
        setBancos(null);
      },
    }),
    [
      token,
      idColaborador,
      userData,
      cargaPerfil,
      postos,
      veiculos,
      pessoas,
      checklists,
      bancos,
      location,
      isHydrated,
      isBiometricsSetupDone,
      permiteSemBiometria,
      fcmToken,
      signIn,
      signOut,
      setUserData,
      refreshUserData,
      refreshStaticData,
      carregarDominioTcred,
      ensureBancosLoaded,
      ensureChecklistsLoaded,
      refreshLocation,
      markBiometricsSetupDone,
      setFcmToken,
      syncFirebaseToken,
      tcClearAllThrottle,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Aviso de LOGOFF REMOTO — mesmo padrão visual dos modais de aviso do
          Registro.tsx (tolerância de jornada / intervalo entre batidas). */}
      {/* WEB: `statusBarTranslucent` não existe no browser (o shim cobre a viewport). */}
      <Modal
        visible={logoffRemotoAviso}
        transparent
        animationType="fade"
        onRequestClose={() => setLogoffRemotoAviso(false)}
      >
        <div className={s.overlay}>
          <div className={s.card}>
            <div className={s.iconWrap}>
              <MaterialIcons name="logout" size={30} color={LOGOFF_ACCENT} />
            </div>
            <span className={s.title}>{T("Sessão encerrada")}</span>
            <span className={s.text}>
              {TJ("Sua sessão foi encerrada pela {0}. Para continuar usando o aplicativo, faça o {1}.", [
                <span className={s.destaque}>{T("administração da sua empresa")}</span>,
                <span className={s.destaque}>{T("login novamente")}</span>,
              ])}
            </span>
            <button
              type="button"
              className={s.primaryBtn}
              onClick={() => setLogoffRemotoAviso(false)}
            >
              <span className={s.primaryBtnText}>{T("Entendi")}</span>
            </button>
          </div>
        </div>
      </Modal>
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }

  return context;
}

// Visual IDÊNTICO aos modais de aviso do Registro.tsx (geofenceOverlay/intervaloCard/
// geofencePrimaryBtn) — valores copiados de lá; mantê-los em sincronia se o padrão mudar.
// WEB: estilos em AuthContext.module.css (classes com os mesmos nomes do logoffStyles).
const LOGOFF_ACCENT = "#f0a15c";

export { AUTH_TOKEN_STORAGE_KEY, AUTH_USER_DATA_KEY };
